
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { Hotel, FilterCriteria } from '../types';
import { useLocalization } from '../hooks/useLocalization';
import { translateFilterValue } from '../constants';

interface FilterSidebarProps {
    results: Hotel[];
    onFilterChange: (filters: FilterCriteria) => void;
    // TravelMate AI props
    isAIEnabled?: boolean;
    isAILoading?: boolean;
    selectedPreferences?: string[];
    onAIToggle?: (enabled: boolean) => void;
    onPreferenceToggle?: (tag: string) => void;
    onAnalyze?: () => void;
}

const AI_PREFERENCE_TAGS = [
    { id: "family", en: "Family Friendly", sv: "Familjevänligt", da: "Familievenligt" },
    { id: "luxury", en: "Luxury", sv: "Lyx", da: "Luksus" },
    { id: "budget", en: "Budget", sv: "Budget", da: "Budget" },
    { id: "nightlife", en: "Nightlife", sv: "Nattliv", da: "Natteliv" },
    { id: "beach", en: "Beach & Pool", sv: "Strand & Pool", da: "Strand & Pool" },
    { id: "city", en: "City Break", sv: "Stadssemester", da: "Storbyferie" },
    { id: "relax", en: "Relaxation", sv: "Avkoppling", da: "Afslapning" },
    { id: "adventure", en: "Adventure", sv: "Äventyr", da: "Eventyr" },
    { id: "food", en: "Gastronomy", sv: "Gastronomi", da: "Gastronomi" },
    { id: "spa", en: "Spa & Wellness", sv: "Spa & Wellness", da: "Spa & Wellness" },
    { id: "romantic", en: "Romantic", sv: "Romantik", da: "Romantik" },
    { id: "adults", en: "Adults Only", sv: "Endast Vuxna", da: "Kun Voksne" }
];

const StarIcon: React.FC<{ filled: boolean }> = ({ filled }) => (
    <svg className={`w-4 h-4 ${filled ? 'text-yellow-400' : 'text-gray-300'}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
);

const ChevronIcon: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
    >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
);

const FilterSection: React.FC<{ title: string; children: React.ReactNode; defaultOpen?: boolean }> = ({ title, children, defaultOpen = true }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="py-4 border-b border-gray-200">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex justify-between items-center group focus:outline-none"
            >
                <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wide group-hover:text-amber-600 transition-colors">{title}</h3>
                <ChevronIcon isOpen={isOpen} />
            </button>
            {isOpen && (
                <div className="mt-3 space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar animate-fade-in">
                    {children}
                </div>
            )}
        </div>
    );
};

const CheckboxFilter: React.FC<{
    label: string | React.ReactNode;
    count: number;
    checked: boolean;
    onChange: (checked: boolean) => void;
}> = ({ label, count, checked, onChange }) => (
    <label className="flex items-center justify-between text-sm text-gray-600 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors group">
        <div className="flex items-center">
            <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
            />
            <span className="ml-3 select-none group-hover:text-gray-900">{label}</span>
        </div>
        <span className="text-xs bg-gray-100 text-gray-500 font-medium rounded-full px-2 py-0.5 group-hover:bg-gray-200">{count}</span>
    </label>
);

// Helper for normalizing text (Exported for SearchResults usage)
export const normalizeFilterText = (text: string | undefined | null): string => {
    if (!text) return '';
    let str = String(text).trim();

    // Normalize spacing around ampersands (e.g., "Bed&Breakfast" -> "Bed & Breakfast")
    str = str.replace(/\s*&\s*/g, ' & ');

    // Keep numbers (like star ratings) as is, but as string
    if (!isNaN(Number(str))) return str;

    // Title Case for strings
    return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

export const FilterSidebar: React.FC<FilterSidebarProps> = ({
    results, onFilterChange,
    isAIEnabled = false, isAILoading = false, selectedPreferences = [],
    onAIToggle, onPreferenceToggle, onAnalyze
}) => {
    const { t, language, currency } = useLocalization();

    // -- State for Filters --
    const [nameSearch, setNameSearch] = useState('');
    const [priceRange, setPriceRange] = useState<[number, number]>([0, 0]);
    const [selectedStars, setSelectedStars] = useState<number[]>([]);
    const [selectedBoards, setSelectedBoards] = useState<string[]>([]);
    const [selectedThemes, setSelectedThemes] = useState<string[]>([]);
    const [selectedFacilities, setSelectedFacilities] = useState<string[]>([]);

    // Mobile Detection for Default Accordion State
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Initial Price Range Calculation
    const globalPrices = useMemo(() => results.map(r => r.pricePerNight).filter(p => p > 0), [results]);
    const globalMin = globalPrices.length > 0 ? Math.floor(Math.min(...globalPrices)) : 0;
    const globalMax = globalPrices.length > 0 ? Math.ceil(Math.max(...globalPrices)) : 10000;

    // Set initial price range only once when results load
    useEffect(() => {
        if (globalPrices.length > 0 && priceRange[1] === 0) {
            setPriceRange([globalMin, globalMax]);
        }
    }, [globalMin, globalMax, globalPrices.length]);

    // Slider Logic
    const minValRef = useRef(globalMin);
    const maxValRef = useRef(globalMax);
    const range = useRef<HTMLDivElement>(null);

    // Convert to percentage
    const getPercent = useCallback(
        (value: number) => Math.round(((value - globalMin) / (globalMax - globalMin)) * 100),
        [globalMin, globalMax]
    );

    // Update range background width
    useEffect(() => {
        if (range.current) {
            const minPercent = getPercent(priceRange[0]);
            const maxPercent = getPercent(priceRange[1]);
            if (range.current) {
                range.current.style.left = `${minPercent}%`;
                range.current.style.width = `${maxPercent - minPercent}%`;
            }
        }
    }, [priceRange[0], priceRange[1], getPercent]);

    // -- Aggregation Logic --
    const aggregateCounts = (extractor: (h: Hotel) => (string | number | undefined)[]) => {
        const counts: { [key: string]: number } = {};
        results.forEach(hotel => {
            const values = extractor(hotel);
            values.forEach(value => {
                if (value !== undefined && value !== null && value !== '') {
                    // Normalize star ratings to integer to group duplicates (e.g. 5.0 and 5)
                    let key = String(value);
                    if (!isNaN(Number(key))) {
                        key = String(Math.floor(Number(key)));
                    } else {
                        key = normalizeFilterText(key);
                    }
                    counts[key] = (counts[key] || 0) + 1;
                }
            });
        });
        return Object.entries(counts).sort((a, b) => b[1] - a[1]);
    };

    const starRatings = useMemo(() => aggregateCounts(h => [h.rating]).sort((a, b) => Number(b[0]) - Number(a[0])), [results]);
    const boards = useMemo(() => aggregateCounts(h => [h.board]), [results]);
    const themes = useMemo(() => aggregateCounts(h => h.themes), [results]);
    const facilities = useMemo(() => aggregateCounts(h => h.facilities), [results]);

    // -- Emit Changes --
    useEffect(() => {
        onFilterChange({
            name: nameSearch,
            priceRange: priceRange,
            stars: selectedStars,
            boards: selectedBoards,
            themes: selectedThemes,
            facilities: selectedFacilities
        });
    }, [nameSearch, priceRange, selectedStars, selectedBoards, selectedThemes, selectedFacilities, onFilterChange]);

    // -- Handlers --
    const toggleSelection = (item: string | number, currentList: any[], setList: (l: any[]) => void) => {
        const newItem = typeof item === 'string' ? item : Number(item);
        if (currentList.includes(newItem)) {
            setList(currentList.filter(i => i !== newItem));
        } else {
            setList([...currentList, newItem]);
        }
    };

    // Default open on desktop, closed on mobile
    const defaultOpenState = !isMobile;

    return (
        <div className="bg-white p-4 rounded-md shadow">
            <div className="flex justify-between items-center mb-2">
                <h2 className="text-md font-bold">{t('search_filters')}</h2>
                <button
                    onClick={() => {
                        setNameSearch('');
                        setPriceRange([globalMin, globalMax]);
                        setSelectedStars([]);
                        setSelectedBoards([]);
                        setSelectedThemes([]);
                        setSelectedFacilities([]);
                    }}
                    className="text-xs text-amber-600 hover:underline"
                >
                    {t('reset_all')}
                </button>
            </div>

            {/* TravelMate AI Section */}
            <FilterSection title="TravelMate AI" defaultOpen={false}>
                {/* AI Toggle Switch */}
                <label className="flex items-center justify-between cursor-pointer mb-3">
                    <span className="text-sm font-medium text-gray-700">
                        {language === 'sv' ? 'AI Analys' : language === 'da' ? 'AI Analyse' : 'AI Analysis'}
                    </span>
                    <div className="relative">
                        <input
                            type="checkbox"
                            className="sr-only"
                            checked={isAIEnabled}
                            onChange={(e) => onAIToggle?.(e.target.checked)}
                        />
                        <div className={`w-11 h-6 rounded-full transition-colors duration-200 ${isAIEnabled ? 'bg-indigo-600' : 'bg-gray-300'
                            }`}>
                            <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${isAIEnabled ? 'translate-x-5' : 'translate-x-0'
                                }`} />
                        </div>
                    </div>
                </label>

                {/* Preference Checkboxes */}
                <div className={`space-y-1 transition-opacity duration-200 ${isAIEnabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                    <p className="text-[11px] text-gray-500 font-medium uppercase tracking-wide mb-2">
                        {language === 'sv' ? 'Resepreferenser' : language === 'da' ? 'Rejsepræferencer' : 'Travel Preferences'}
                    </p>
                    {AI_PREFERENCE_TAGS.map(tag => (
                        <label key={tag.id} className="flex items-center text-sm text-gray-600 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors group">
                            <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                checked={selectedPreferences.includes(tag.id)}
                                onChange={() => onPreferenceToggle?.(tag.id)}
                                disabled={!isAIEnabled}
                            />
                            <span className="ml-3 select-none group-hover:text-gray-900">
                                {(tag as any)[language] || tag.en}
                            </span>
                        </label>
                    ))}
                </div>

                {/* AI Loading Indicator (auto-triggers, no button needed) */}
                {isAILoading && (
                    <div className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold bg-indigo-100 text-indigo-400">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {language === 'sv' ? 'Analyserar...' : language === 'da' ? 'Analyserer...' : 'Analyzing...'}
                    </div>
                )}
            </FilterSection>

            <FilterSection title={t('hotel_search')} defaultOpen={true}>
                <input
                    type="text"
                    placeholder="e.g. Plaza"
                    value={nameSearch}
                    onChange={(e) => setNameSearch(e.target.value)}
                    className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm text-black focus:outline-none focus:border-amber-500"
                />
            </FilterSection>

            <FilterSection title={t('hotel_price')} defaultOpen={defaultOpenState}>
                <div className="relative pt-6 pb-2">
                    <input
                        type="range"
                        min={globalMin}
                        max={globalMax}
                        value={priceRange[0]}
                        onChange={(event) => {
                            const value = Math.min(Number(event.target.value), priceRange[1] - 1);
                            setPriceRange([value, priceRange[1]]);
                        }}
                        className="thumb thumb--left"
                        style={{ zIndex: priceRange[0] > globalMax - 100 ? 5 : undefined }}
                    />
                    <input
                        type="range"
                        min={globalMin}
                        max={globalMax}
                        value={priceRange[1]}
                        onChange={(event) => {
                            const value = Math.max(Number(event.target.value), priceRange[0] + 1);
                            setPriceRange([priceRange[0], value]);
                        }}
                        className="thumb thumb--right"
                    />

                    <div className="relative w-full">
                        <div className="slider__track absolute bg-gray-200 h-1.5 rounded w-full z-0" />
                        <div ref={range} className="slider__range absolute bg-amber-500 h-1.5 rounded z-10" />
                    </div>
                </div>

                <div className="flex justify-between mt-4">
                    <div className="border border-gray-300 rounded px-2 py-1 text-xs text-gray-700 bg-gray-50 min-w-[60px] text-center">
                        {currency === 'SEK' || currency === 'DKK' ? `${priceRange[0]} kr` : `€${priceRange[0]}`}
                    </div>
                    <div className="border border-gray-300 rounded px-2 py-1 text-xs text-gray-700 bg-gray-50 min-w-[60px] text-center">
                        {currency === 'SEK' || currency === 'DKK' ? `${priceRange[1]} kr` : `€${priceRange[1]}`}
                    </div>
                </div>

                <style>{`
                    .thumb {
                        pointer-events: none;
                        position: absolute;
                        height: 0;
                        width: 100%;
                        outline: none;
                        z-index: 2; /* below range but above track */
                        -webkit-appearance: none;
                    }
                    .thumb::-webkit-slider-thumb {
                        -webkit-appearance: none;
                        background-color: white;
                        border: 2px solid #f59e0b; /* Amber 500 */
                        border-radius: 50%;
                        cursor: pointer;
                        height: 18px;
                        width: 18px;
                        margin-top: 4px;
                        pointer-events: all;
                        position: relative;
                        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                        z-index: 5;
                    }
                    .thumb::-moz-range-thumb {
                        background-color: white;
                        border: 2px solid #f59e0b;
                        border-radius: 50%;
                        cursor: pointer;
                        height: 18px;
                        width: 18px;
                        pointer-events: all;
                        position: relative;
                        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                        z-index: 5;
                    }
                `}</style>
            </FilterSection>

            <FilterSection title={t('hotel_stars')} defaultOpen={defaultOpenState}>
                {starRatings.map(([ratingStr, count]) => {
                    const rating = Number(ratingStr);
                    if (isNaN(rating)) return null;
                    return (
                        <CheckboxFilter
                            key={rating}
                            count={count}
                            checked={selectedStars.includes(rating)}
                            onChange={() => toggleSelection(rating, selectedStars, setSelectedStars)}
                            label={<span className="flex">{[...Array(5)].map((_, i) => <StarIcon key={i} filled={i < rating} />)}</span>}
                        />
                    );
                })}
            </FilterSection>

            {boards.length > 0 && (
                <FilterSection title={t('hotel_boards')} defaultOpen={defaultOpenState}>
                    {boards.slice(0, 8).map(([board, count]) => (
                        <CheckboxFilter
                            key={board}
                            count={count}
                            label={translateFilterValue(board, language)}
                            checked={selectedBoards.includes(board)}
                            onChange={() => toggleSelection(board, selectedBoards, setSelectedBoards)}
                        />
                    ))}
                </FilterSection>
            )}

            {themes.length > 0 && (
                <FilterSection title={t('hotel_themes')} defaultOpen={defaultOpenState}>
                    {themes.slice(0, 10).map(([theme, count]) => (
                        <CheckboxFilter
                            key={theme}
                            count={count}
                            label={translateFilterValue(theme, language)}
                            checked={selectedThemes.includes(theme)}
                            onChange={() => toggleSelection(theme, selectedThemes, setSelectedThemes)}
                        />
                    ))}
                </FilterSection>
            )}

            {facilities.length > 0 && (
                <FilterSection title={t('hotel_facilities')} defaultOpen={false}>
                    {facilities.slice(0, 12).map(([facility, count]) => (
                        <CheckboxFilter
                            key={facility}
                            count={count}
                            label={translateFilterValue(facility, language)}
                            checked={selectedFacilities.includes(facility)}
                            onChange={() => toggleSelection(facility, selectedFacilities, setSelectedFacilities)}
                        />
                    ))}
                </FilterSection>
            )}
        </div>
    );
};
